import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Avatar,
    Grid,
    Card,
    CardContent
} from '@mui/material';
import { Save, Security, Notifications, Person, Business, Message } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppTest from '../components/WhatsAppTest';

const AdminSettings: React.FC = () => {
    const { user } = useAuth();
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showWhatsAppTest, setShowWhatsAppTest] = useState(false);

    // Settings state
    const [settings, setSettings] = useState({
        emailNotifications: true,
        whatsappNotifications: true,
        marketingEmails: false,
        businessName: '',
        phone: ''
    });

    // Load settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get('/api/settings');
                setSettings(response.data);
            } catch (err: any) {
                console.error('Error loading settings:', err);
                // Use default settings if error
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            await axios.post('/api/settings', settings);
            setSuccess('Configuración guardada exitosamente');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err: any) {
            console.error('Error saving settings:', err);
            setError('Error al guardar la configuración');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <SettingsIconWrapper />
                Configuración
            </Typography>

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Profile Section */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                            <Avatar
                                sx={{ width: 100, height: 100, mb: 2, bgcolor: 'primary.main', fontSize: 40 }}
                            >
                                {user?.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="h5" gutterBottom>
                                {user?.name}
                            </Typography>
                            <Typography color="text.secondary" gutterBottom>
                                {user?.email}
                            </Typography>
                            <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                                Cambiar Avatar
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* General Settings */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" />
                            Información del Negocio
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nombre del Negocio"
                                    value={settings.businessName}
                                    onChange={(e) => setSettings({ ...settings, businessName: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Teléfono de Contacto"
                                    value={settings.phone}
                                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Notifications fontSize="small" />
                            Notificaciones
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.emailNotifications}
                                        onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                                    />
                                }
                                label="Recibir notificaciones por Email"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.whatsappNotifications}
                                        onChange={(e) => setSettings({ ...settings, whatsappNotifications: e.target.checked })}
                                    />
                                }
                                label="Recibir notificaciones por WhatsApp"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.marketingEmails}
                                        onChange={(e) => setSettings({ ...settings, marketingEmails: e.target.checked })}
                                    />
                                }
                                label="Suscribirse al boletín de novedades"
                            />
                            <Divider sx={{ my: 2 }} />

                        </Box>
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Security fontSize="small" />
                            Seguridad
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Button variant="outlined" color="primary">
                            Cambiar Contraseña
                        </Button>
                    </Paper>

                    {showWhatsAppTest && (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <WhatsAppTest onClose={() => setShowWhatsAppTest(false)} />
                        </Paper>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );
};

const SettingsIconWrapper = () => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'primary.main'
        }}
    >
        <Person />
    </Box>
);

export default AdminSettings;
